import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import toast from 'react-hot-toast';
import { 
  FormBox, 
  FormTitle, 
  Input, 
  Button 
} from '../common/Form.styles';
import { StyledSelect } from './AddUserPage.styles';

// We need the raw firebase config to create a temporary app instance
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const AddUserPage = () => {
    const navigate = useNavigate();

    const formik = useFormik({
        initialValues: {
            clientName: '',
            email: '',
            password: '',
            role: 'data-entry'
        },
        validationSchema: Yup.object({
            clientName: Yup.string().required('Name is required'),
            email: Yup.string().email('Invalid email address').required('Required'),
            password: Yup.string().min(6, 'Password must be at least 6 characters').required('Required'),
            role: Yup.string().oneOf(['data-entry', 'admin']).required('Role is required')
        }),
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            const toastId = toast.loading('Creating new user...');
            
            const tempAppName = `auth-worker-${Date.now()}`;
            const tempApp = initializeApp(firebaseConfig, tempAppName);
            const tempAuth = getAuth(tempApp);

            try {
                const userCredential = await createUserWithEmailAndPassword(tempAuth, values.email, values.password);
                const newUser = userCredential.user;

                await setDoc(doc(db, "users", newUser.uid), {
                    clientName: values.clientName,
                    email: values.email,
                    role: values.role
                });

                toast.success('User created successfully!', { id: toastId });
                resetForm();
                navigate('/admin/users');

            } catch (error) {
                const errorMessage = error.code === 'auth/email-already-in-use' 
                    ? 'This email is already in use.' 
                    : 'Failed to create user.';
                toast.error(errorMessage, { id: toastId });
                console.error("User creation error:", error);
            } finally {
                await deleteApp(tempApp);
                setSubmitting(false);
            }
        }
    });

    return (
        <div>
            {/* The title is now inside the FormBox for better centering */}
            <FormBox>
                <FormTitle>Add New User</FormTitle>
                <form onSubmit={formik.handleSubmit}>
                    <Input name="clientName" placeholder="Full Name" onChange={formik.handleChange} value={formik.values.clientName}/>
                    <Input name="email" type="email" placeholder="Email Address" onChange={formik.handleChange} value={formik.values.email} />
                    <Input name="password" type="password" placeholder="Password" onChange={formik.handleChange} value={formik.values.password}/>
                    <StyledSelect name="role" value={formik.values.role} onChange={formik.handleChange}>
                        <option value="data-entry">Data-Entry</option>
                        <option value="admin">Admin</option>
                    </StyledSelect>
                    <Button type="submit" disabled={formik.isSubmitting}>
                        {formik.isSubmitting ? 'Creating...' : 'Create User'}
                    </Button>
                </form>
            </FormBox>
        </div>
    );
};

export default AddUserPage;